/* eslint-disable @next/next/no-img-element */
// Shared building blocks for the landing-page product visuals.
//
// Each visual is laid out on a fixed design canvas (e.g. 1448×1086, the size
// of the PNG it replaces) and every dimension is expressed in "design px"
// through `unit(W)`, which maps to container-query width units. The canvas is
// the query container, so the whole visual scales like an image with no JS,
// while text, shapes and charts stay vector-sharp at any size and DPR.

export const unit = (W) => (n) => `calc(${n} * 100cqw / ${W})`;

export const C = {
  ink: '#1C1830',
  inkSoft: '#4A4560',
  mute: '#8A869C',
  line: '#ECEAF2',
  card: '#FFFFFF',
  sidebar: '#211A3D',
  purple: '#6D4AFF',
  purpleSoft: '#EFEBFF',
  blue: '#3B6FE0',
  green: '#22A84A',
  greenSoft: '#E6F6EA',
  red: '#E5484D',
  orange: '#F08A2C',
  orangeSoft: '#FFF1E2',
  coral: '#F26B3A',
};

export function Canvas({ W, H, label, className = '', style, children }) {
  return (
    <div
      role="img"
      aria-label={label}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${W} / ${H}`,
        containerType: 'inline-size',
        fontFamily: 'var(--bh-font-body)',
        color: C.ink,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        ...style,
      }}
    >
      {/* Base font size in design px, so inherited line boxes scale too
          (cqw can't be used on the container element itself). */}
      <div style={{ position: 'absolute', inset: 0, fontSize: unit(W)(16), fontWeight: 500 }}>
        {children}
      </div>
    </div>
  );
}

// Absolutely positioned box in design px.
export function Abs({ u, x, y, w, h, style, children, ...rest }) {
  return (
    <div
      {...rest}
      style={{
        position: 'absolute',
        left: u(x),
        top: u(y),
        width: w != null ? u(w) : undefined,
        height: h != null ? u(h) : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Card({ u, x, y, w, h, r = 20, bg = C.card, style, children }) {
  return (
    <Abs
      u={u} x={x} y={y} w={w} h={h}
      style={{
        background: bg,
        borderRadius: u(r),
        border: `${u(1.5)} solid ${C.line}`,
        boxShadow: `0 ${u(10)} ${u(30)} rgba(40, 30, 80, 0.10), 0 ${u(2)} ${u(6)} rgba(40, 30, 80, 0.05)`,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </Abs>
  );
}

export function Avatar({ u, name, size, ring, dot, style }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: u(size), height: u(size), flexShrink: 0, ...style }}>
      <img
        src={`/img/redesign/avatars/${name}.webp`}
        alt=""
        draggable="false"
        style={{
          width: '100%', height: '100%', borderRadius: '50%', display: 'block',
          boxShadow: ring ? `0 0 0 ${u(ring)} #fff` : undefined,
        }}
      />
      {dot && (
        <span style={{
          position: 'absolute', right: u(-1), bottom: u(-1),
          width: u(size * 0.3), height: u(size * 0.3), borderRadius: '50%',
          background: '#22C55E', border: `${u(size * 0.06)} solid ${dot}`,
        }} />
      )}
    </span>
  );
}

// "b" brand tile, same as the favicon.
export function BLogo({ u, size }) {
  return (
    <span style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: u(size), height: u(size), borderRadius: u(size * 0.24),
      background: '#15111F', color: '#FF9F1C',
      fontFamily: 'var(--bh-font-display)', fontWeight: 600,
      fontSize: u(size * 0.78), lineHeight: 1, paddingBottom: u(size * 0.08),
      boxSizing: 'border-box',
    }}>b</span>
  );
}

// Lucide-style stroke icons, drawn on a 24×24 grid.
const ICONS = {
  home: <path d="M3 10.2 12 3l9 7.2V20a1 1 0 0 1-1 1h-5.5v-6.5h-5V21H4a1 1 0 0 1-1-1z" fill="currentColor" stroke="none" />,
  bot: (
    <>
      <path d="M12 3a8.5 8.5 0 0 0-7.6 12.3L3 21l5.9-1.3A8.5 8.5 0 1 0 12 3z" fill="currentColor" stroke="none" />
      <rect x="6.5" y="8.2" width="11" height="7" rx="3.5" fill="#211A3D" stroke="none" />
      <circle cx="9.6" cy="11.7" r="1.25" fill="currentColor" stroke="none" />
      <circle cx="14.4" cy="11.7" r="1.25" fill="currentColor" stroke="none" />
    </>
  ),
  chat: (
    <>
      <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 8 2.5 8h-17S6 15 6 9z" fill="currentColor" stroke="none" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    </>
  ),
  hash: <path d="M4.5 9h15M4.5 15h15M10 3.5 8 20.5M16 3.5l-2 17" />,
  plus: <path d="M12 4.5v15M4.5 12h15" />,
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" fill="currentColor" stroke="none" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6z" fill="currentColor" stroke="none" />
      <circle cx="17" cy="8.5" r="2.8" fill="currentColor" stroke="none" />
      <path d="M16 13.6c3.3-.3 5.6 1.9 5.6 5.4H17" fill="currentColor" stroke="none" />
    </>
  ),
  chart: <path d="M5 20v-6M12 20V9M19 20V4" strokeWidth="3.2" />,
  gear: (
    <>
      <path d="M12 2.8l1.6 2.3 2.7-.7.6 2.7 2.6 1-.9 2.6 1.9 2-2.1 1.8.6 2.7-2.7.4-1 2.6-2.5-1.2-2.3 1.6-1.4-2.4-2.8-.1.1-2.8-2.4-1.4 1.6-2.3-1.2-2.5 2.6-1 .4-2.7 2.7.6z" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="3.3" fill="#211A3D" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5M12 7.6v.1" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  monitor: (
    <>
      <rect x="2.5" y="3.5" width="19" height="13.5" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </>
  ),
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.2 14.2s1.4 2.1 3.8 2.1 3.8-2.1 3.8-2.1M9 9.5v.1M15 9.5v.1" />
    </>
  ),
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
  arrowUp: <path d="M12 19V5M6 11l6-6 6 6" />,
  arrowDown: <path d="M12 5v14M6 13l6 6 6-6" />,
  chevronDown: <path d="M6 9l6 6 6-6" />,
  star: <path d="M12 2.8l2.8 5.8 6.3.8-4.6 4.4 1.1 6.3L12 17.1l-5.6 3 1.1-6.3L2.9 9.4l6.3-.8z" fill="currentColor" stroke="none" />,
};

export function Icon({ u, name, size, color = 'currentColor', stroke = 2, style }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={undefined}
      style={{ width: u(size), height: u(size), display: 'block', flexShrink: 0, color, ...style }}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

// Dark app sidebar used by every Slack-like mock.
// `items` is a list of icon names; `active` is the index of the purple tile.
export function Sidebar({ u, x, y, w, h, items, active, avatar = 'me', itemTop, itemGap, frame = 13 }) {
  const inner = w - frame * 2;
  return (
    <Abs u={u} x={x} y={y} w={w} h={h} style={{
      background: '#FBFAFE', borderRadius: u(24), boxSizing: 'border-box',
      border: `${u(1.5)} solid ${C.line}`,
      boxShadow: `0 ${u(10)} ${u(30)} rgba(40, 30, 80, 0.10)`,
    }}>
      <Abs u={u} x={frame - 1.5} y={frame - 1.5} w={inner} h={h - frame * 2} style={{
        background: C.sidebar, borderRadius: u(18),
      }}>
        <Abs u={u} x={(inner - 50) / 2} y={12}><BLogo u={u} size={50} /></Abs>
        {items.map((name, i) => {
          const isActive = i === active;
          return (
            <Abs key={i} u={u} x={(inner - 50) / 2} y={itemTop + i * itemGap - 25} w={50} h={50} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: u(12), background: isActive ? C.purple : 'transparent', color: '#fff',
            }}>
              <Icon u={u} name={name} size={30} stroke={2.4} />
            </Abs>
          );
        })}
        {avatar && (
          <Abs u={u} x={(inner - 54) / 2} y={h - frame * 2 - 86}>
            <Avatar u={u} name={avatar} size={54} dot={C.sidebar} />
          </Abs>
        )}
      </Abs>
    </Abs>
  );
}

// Dashed curved connector arrows, drawn in one vector overlay.
// Each arrow: { d: 'svg path', color, head: [x, y, angleDeg], tail?: [x, y, angleDeg] }
export function Arrows({ W, H, arrows }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} aria-hidden="true">
      {arrows.map((a, i) => (
        <g key={i} stroke={a.color} fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d={a.d} strokeDasharray="7 9" />
          {[a.head, a.tail].filter(Boolean).map(([hx, hy, deg], j) => (
            <path key={j} d="M-11 -9 L0 0 L-11 9" transform={`translate(${hx} ${hy}) rotate(${deg})`} strokeWidth="3.5" />
          ))}
        </g>
      ))}
    </svg>
  );
}

export function Sparkle({ u, x, y, size, color }) {
  return (
    <Abs u={u} x={x - size / 2} y={y - size / 2} w={size} h={size}>
      <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
        <path d="M12 1.5c.7 5.6 4.9 9.8 10.5 10.5-5.6.7-9.8 4.9-10.5 10.5C11.3 16.9 7.1 12.7 1.5 12 7.1 11.3 11.3 7.1 12 1.5z" fill={color} />
      </svg>
    </Abs>
  );
}
